import React, {useCallback, useEffect, useMemo, useState} from 'react';
import dateFormat from 'dateformat';

// STYLEDS
import * as S from './SearchWidgetUI.styles';
import * as styles from '../Autosuggests/SearchWidget/SearchWidgetAutosuggest.styles';

// COMPONENTS
import SearchWidgetAutosuggest from '../Autosuggests/SearchWidget/SearchWidgetAutosuggest';
import CalendarBlock from '../../HomePage/CalendarBlock/CalendarBlock';
import PaxWidget from '../../HomePage/PaxWidget/PaxWidget';
import CalenderPickerNew from '../../../../../shared/components/CalendarPickerNew/CalendarPickerNew';
import CalendarComponentChunk from '../../../../../shared/components/CalendarPickerNew';
// UTILS
import {convertRoomGuestStringToReadable} from '../../../../../shared/utils/room-string-utils';
import {appConfig} from '@stencil/hotels-config';
import {getUserTimeZoneOffset} from '@stencil/hotels-utils/src/common-utils';

// HOOKS
import useSearchWidgetUI from '../../../../../shared/hooks/useSearchWidgetUI';
import useIsClient from '@stencil/hotels-hooks/src/useIsClient';

import {FlexItem} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import {gaCheckinDateDifference} from '@stencil/hotels-utils/src/date-utils/date-utils';

import {pushga} from '@stencil/ga-utils';
import {gaConfig} from '@stencil/hotels-config';
import {SearchWidgetTrack} from '@stencil/omniture-analytics/src/constants/common';
import {getCmpCookieValue} from '@stencil/hotels-utils/src/common-utils';

const {SrpGAEvents, detailsPageClickEventCategoryName} = gaConfig;
const {defaultRoomString} = appConfig;

const SearchWidgetUI = ({
  updateQueryCaller,
  autosuggestHandler,
  roomString,
  checkin,
  checkout,
  searchInputValue,
  updateSearch,
  gaHandler,
  setSearchWidgetInputState,
  mode,
  destinationChanged,
  isSeoSrp,
  isSeoDetail,
  isOpenCalendarOnDweb,
  cityId,
  dispatch,
  userCityPaxLength,
  isDetail,
  pushOmnitureTracking,
  maxChildAge,
  setParentCalMode = (mode?: string) => {},
  isDatePickRequired: isDatePickRequiredProps = false,
  callLandingandSearchApi = ({}) => {},
  multiRoomConfig,
  appliedFiltersDispatch,
  appliedFiltersObj,
  isNewCalendarPicker,
  isSRPSEOV2 = false,
  isDateUpdatedForSEOUnif,
}) => {
  const initailCalendarMode = isSeoDetail ? 'checkin' : '';

  const {
    calendarBlockRef,
    paxWidgetBlockRef,
    calendarMode,
    isCalendarClose,
    closeCalendar,
    setCalendarMode,
    isPaxWidgetVisible,
    togglePaxWidget,
  } = useSearchWidgetUI({initailCalendarMode});

  const cmpCookieValue = getCmpCookieValue();
  const isSeoFlow = cmpCookieValue?.toUpperCase()?.startsWith('SEO');

  const isClient = useIsClient();
  const [isDateRequired, setDateRequired] = useState(!isClient && isSeoDetail);
  const [isPaxRequired, setPaxRequired] = useState(false);

  useEffect(() => {
    if (isDatePickRequiredProps) {
      setDateRequired(true);
      setPaxRequired(true);
    }
  }, [isDatePickRequiredProps]);

  const isCalendarVisible = calendarMode !== '';
  const getNumberOfNights = ({co_date = null, checkin = new Date(), checkout = new Date()}) => {
    const num = Math.floor(
      ((co_date ? co_date.getTime() : checkout.getTime()) - checkin.getTime()) / (1000 * 3600 * 24),
    );
    return num;
  };

  useEffect(() => {
    if (isSeoDetail && isDateUpdatedForSEOUnif) {
      setCalendarMode('');
      setDateRequired(false);
    }
  }, [isDateUpdatedForSEOUnif, isSeoDetail]);

  useEffect(() => {
    if (isOpenCalendarOnDweb) {
      let eventLabel = '';
      if (userCityPaxLength === 6) {
        eventLabel = 'sixth_city';
      } else {
        eventLabel = `first_visit`;
      }
      pushga({
        event: isDetail ? detailsPageClickEventCategoryName : SrpGAEvents.hotelSrpClicks,
        eventCategory: isDetail ? detailsPageClickEventCategoryName : SrpGAEvents.hotelSrpClicks,
        eventAction: SrpGAEvents.action.openCalendar,
        eventLabel: `${eventLabel}_${cityId}`,
      });
    }
  }, [isOpenCalendarOnDweb]);

  const renderDate = useCallback(
    (date: string) => {
      if (!checkin || !checkout) return '';
      if (!isSeoSrp && !isSeoDetail) {
        return date;
      } else if (__SERVER__) {
        return '';
      } else {
        return date;
      }
    },
    [checkin, isSeoDetail, isSeoSrp],
  );

  const selectedRoomString = useMemo(() => {
    const finalRoomString = roomString || defaultRoomString;
    if (!isSeoSrp && !isSeoDetail) {
      return finalRoomString;
    } else if (__SERVER__) {
      return '';
    } else {
      return finalRoomString;
    }
  }, [isSeoDetail, isSeoSrp, roomString, defaultRoomString]);

  const getLOS = inputDate => {
    let los = gaCheckinDateDifference(inputDate);
    los = los < 0 ? 0 : los + 1;
    return los;
  };
  useEffect(() => {
    if (mode && (mode == 'checkin' || mode == 'checkout')) {
      setCalendarMode(mode);
    }
  }, [mode]);

  useEffect(() => {
    if (calendarMode === '') {
      setParentCalMode(calendarMode);
    } else if (!!calendarMode) {
      togglePaxWidget(false);
    }
  }, [calendarMode]);

  const updateSearchButton = () => {
    if (isCalendarVisible) closeCalendar();

    if (updateSearch) updateSearch(true);
    if (callLandingandSearchApi && typeof callLandingandSearchApi === 'function') {
      callLandingandSearchApi({});
    }
  };

  const updateSearchHandler = () => {
    closeCalendar();
    if (roomString && !isPaxRequired) {
      updateSearch();
      updateQueryCaller({
        isDatePickRequired: false,
      });
    } else {
      togglePaxWidget(true);
    }
  };

  const handleOnSelectDate = useCallback(
    (checkIn, checkOut, isCheckInSelected) => {
      if (isCheckInSelected) {
        // ga event for checkIn
        let apWindow = getLOS(checkIn);
        gaHandler('checkInCalendarClicked', apWindow);
      }
      if (checkIn && checkOut) {
        setDateRequired(false);
        // ga event for checkOut if both checkIn and checkOut are selected
        let los = getNumberOfNights({checkin: checkIn, checkout: checkOut});
        gaHandler('checkOutCalendarClicked', los);

        pushOmnitureTracking(SearchWidgetTrack.CheckInCheckOut, `${checkIn}_${checkOut}`);
        updateSearchHandler();
      }
    },
    [
      setDateRequired,
      isPaxRequired,
      getLOS,
      gaHandler,
      getNumberOfNights,
      closeCalendar,
      updateSearch,
      pushOmnitureTracking,
      roomString,
      togglePaxWidget,
    ],
  );

  return (
    <S.OuterWrapDiv>
      <S.WrapperStyle>
        <FlexItem>
          <S.SearchWrapperArea>
            {(searchInputValue && (
              <SearchWidgetAutosuggest
                isSRPSEOV2={isSRPSEOV2}
                destinationChanged={destinationChanged}
                initialInputValue={searchInputValue}
                onItemSelection={(changes, inputValue, highlightedIndex) => {
                  setSearchWidgetInputState(changes);
                  autosuggestHandler(changes);

                  let eventLabel = `${inputValue ? `${inputValue}_` : ''}${
                    highlightedIndex === -1 ? '' : `${highlightedIndex}_`
                  }${changes.selectedItem?.n}`;
                  const ctaValue = changes.selectedItem.isChild
                    ? `${eventLabel}_${changes.selectedItem?.parentType}_${changes.selectedItem?.t}`
                    : `${eventLabel}_${changes.selectedItem?.t}`;

                  gaHandler(
                    'location',
                    changes.selectedItem.isChild
                      ? `${changes.selectedItem?.parentType}_${changes.selectedItem?.t}|${changes?.selectedItem?.n}`
                      : `${changes.selectedItem?.t}|${changes?.selectedItem?.n}`,
                    {
                      cdautosuggestKeyword: changes.selectedItem.isChild
                        ? changes.selectedItem?.parentType
                        : changes.selectedItem?.t,
                    },
                  );
                  pushOmnitureTracking(SearchWidgetTrack.Location, ctaValue, {
                    type: changes.selectedItem?.t,
                  });
                }}
                isDetail={isDetail}
              />
            )) || (
              <styles.AutocompleteWrapperStyles>
                <styles.SearchLabelStyles>AREA, LANDMARK OR PROPERTY NAME</styles.SearchLabelStyles>
                <styles.SearchInputStyles placeholder="LOCATION NAME" />
              </styles.AutocompleteWrapperStyles>
            )}
          </S.SearchWrapperArea>
          <S.CheckinCheckoutWrapper id="search-widget-calendar-element">
            <div
              onClick={() => {
                setCalendarMode('checkin');
                if (isPaxWidgetVisible) togglePaxWidget(false);
              }}
            >
              <S.SearchLabelStyle>CHECKIN</S.SearchLabelStyle>
              <S.SearchInputStyle
                type="text"
                value={
                  checkin && !isDateRequired
                    ? renderDate(dateFormat(checkin.date, 'mediumDate'))
                    : 'Select date'
                }
                placeholder="Travel Dates"
                id="search-widget-checkin-input"
                editable="false"
                readOnly
              />
            </div>
            {((calendarMode === 'checkin' || calendarMode === 'checkout') &&
              (isNewCalendarPicker && !isSeoFlow ? (
                <S.CalendarBlockWrapper>
                  <CalendarComponentChunk
                    updateQueryCaller={updateQueryCaller}
                    onSelection={updateSearchHandler}
                    ref={calendarBlockRef}
                  />
                </S.CalendarBlockWrapper>
              ) : (
                <CalendarBlock
                  ref={calendarBlockRef}
                  isSearchWidget={true}
                  onSelection={handleOnSelectDate}
                  updateQueryCaller={updateQueryCaller}
                  checkin={!isDateRequired ? checkin : null}
                  checkout={!isDateRequired ? checkout : null}
                  styles={{'margin-top': '1rem'}}
                  mode={calendarMode}
                />
              ))) ||
              null}
          </S.CheckinCheckoutWrapper>
          <S.CheckinCheckoutWrapper>
            <div
              onClick={() => {
                setCalendarMode('checkout');
                if (isPaxWidgetVisible) togglePaxWidget(false);
              }}
            >
              <S.SearchLabelStyle>CHECKOUT</S.SearchLabelStyle>
              <S.SearchInputStyle
                type="text"
                value={
                  checkout && !isDateRequired
                    ? renderDate(dateFormat(checkout.date, 'mediumDate'))
                    : 'Select date'
                }
                placeholder="Travel Dates"
                editable="false"
                readOnly
              />
            </div>
          </S.CheckinCheckoutWrapper>

          <S.PaxWrapperStyle
            onClick={() => {
              if (isCalendarVisible) {
                closeCalendar();
              }

              if (!isPaxWidgetVisible) {
                togglePaxWidget(true);
              }
            }}
          >
            <S.SearchLabelStyle>GUEST &amp; ROOMS</S.SearchLabelStyle>
            <S.SearchInputStyle
              type="text"
              value={
                isPaxRequired && !isPaxWidgetVisible
                  ? 'Select Guest and rooms'
                  : convertRoomGuestStringToReadable(selectedRoomString, '.')
              }
              editable="false"
              readOnly
            />
            {(isPaxWidgetVisible && selectedRoomString && (
              <PaxWidget
                ref={paxWidgetBlockRef}
                togglePaxWidget={togglePaxWidget}
                updateQueryCaller={updateQueryCaller}
                roomString={selectedRoomString}
                gaHandler={gaHandler}
                updateSearch={updateSearch}
                maxChildAge={maxChildAge}
                pushOmnitureTracking={val => pushOmnitureTracking(SearchWidgetTrack.Pax, val)}
                setCalendarMode={setCalendarMode}
                setPaxRequired={setPaxRequired}
                isDateRequired={isDateRequired}
                multiRoomConfig={multiRoomConfig}
                appliedFiltersDispatch={appliedFiltersDispatch}
                appliedFiltersObj={appliedFiltersObj}
                isDetailPage={isDetail}
              />
            )) ||
              ''}
          </S.PaxWrapperStyle>
        </FlexItem>
        <S.UpdateSearchBtn disabled={isDateRequired || isPaxRequired} onClick={updateSearchButton}>
          Update Search
        </S.UpdateSearchBtn>
      </S.WrapperStyle>
    </S.OuterWrapDiv>
  );
};

export default SearchWidgetUI;
